import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layers, Plus, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import QaSampleReseedDialog from "@/components/studybuilds/QaSampleReseedDialog";
import StudyBuildModal from "@/components/studybuilds/StudyBuildModal";
import StudyBuildFilters from "@/components/studybuilds/StudyBuildFilters";
import StudyBuildDetail, { StatusChip, BuildActions } from "@/components/studybuilds/StudyBuildDetail";

// SiteCentric superdomain — default client for QA-mode study builds.
const QA_CLIENT_ID = "sitecentric";
const ACTIVE = new Set(["queued", "ingesting", "composing"]);

export default function StudyBuildsPage({ qaMode = false }) {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [items, setItems] = useState([]);
    const [facets, setFacets] = useState({ companies: [], counts: {} });
    const [company, setCompany] = useState(qaMode && !isSuper ? QA_CLIENT_ID : "");
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [reseedOpen, setReseedOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "500" });
            if (company) params.set("client_id", company);
            if (status) params.set("status", status);
            const { data } = await api.get(`/admin/study-assets/study-builds?${params}`);
            setItems(data.items || []);
            setFacets({ companies: data.companies || [], counts: data.counts || {} });
        } catch (e) { if (!silent) toast.error(formatApiErrorDetail(e)); }
        finally { if (!silent) setLoading(false); }
    }, [company, status]);
    useEffect(() => { load(); }, [load]);

    // Poll while any build is still running.
    const running = items.some((b) => ACTIVE.has(b.status));
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(() => load(true), 4000);
        return () => clearInterval(t);
    }, [running, load]);

    return (
        <div className="px-8 py-8" data-testid="study-builds-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">{qaMode ? "Quality Assurance" : "Clinical Operations"}</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">{qaMode ? "Study Build (QA)" : "Study Builds"}</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Upload a protocol + budget (+ CTA) or pick documents already in the Data Lake. The build follows the protocol’s
                        schedule of events, prices every visit from the budget and lists each protocol ↔ budget disagreement.
                        SiteCentric runs the same build through <span className="font-mono text-[#c7a6ff]">POST /api/v1/study-builds</span> with the client token.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {qaMode && (
                        <button data-testid="study-build-reseed-btn" onClick={() => setReseedOpen(true)}
                            className="px-4 py-2.5 rounded-full border border-white/15 hover:border-[#7C3AED]/60 hover:bg-[#7C3AED]/10 text-white/80 text-sm font-medium flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" /> Re-ingest sample data
                        </button>
                    )}
                    <button data-testid="study-build-new-btn" onClick={() => setOpen(true)}
                        className="px-4 py-2.5 rounded-full bg-[#7C3AED] hover:bg-[#9667ee] text-white text-sm font-medium flex items-center gap-2">
                        <Plus className="w-4 h-4" /> New Build
                    </button>
                </div>
            </div>

            <StudyBuildFilters companies={facets.companies} counts={facets.counts} company={company} status={status} shown={items.length}
                onCompany={setCompany} onStatus={setStatus} canPickCompany={isSuper || !qaMode} />
            <div className="rounded-2xl border border-white/10 overflow-hidden">
                {loading ? <div className="p-8 text-white/50">Loading…</div>
                    : items.length === 0 ? (
                        <div className="p-12 text-center text-white/50" data-testid="study-builds-empty">
                            <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            {company || status ? "No study builds match these filters." : "No study builds yet."}
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="study-builds-table">
                            <thead className="bg-white/[0.02] text-white/50">
                                <tr>
                                    <th className="text-left px-4 py-3 font-normal">Study</th>
                                    <th className="text-left px-4 py-3 font-normal">Status</th>
                                    <th className="text-left px-4 py-3 font-normal">Client</th>
                                    <th className="text-left px-4 py-3 font-normal">Sources</th>
                                    <th className="text-right px-4 py-3 font-normal">Visits</th>
                                    <th className="text-right px-4 py-3 font-normal">Grand total</th>
                                    <th className="text-left px-4 py-3 font-normal">Discrepancies</th>
                                    <th className="text-left px-4 py-3 font-normal">Created</th>
                                    <th className="px-4 py-3 font-normal" />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((b) => <Row key={b.id} b={b} onOpen={() => setDetailId(b.id)} onChanged={(d) => setItems((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d, build: undefined } : x)))} />)}
                            </tbody>
                        </table>
                    )}
            </div>

            {open && <StudyBuildModal qaMode={qaMode} defaultClientId={qaMode ? QA_CLIENT_ID : ""} onClose={() => setOpen(false)} onDone={(d) => { setOpen(false); load(); if (d?.id) setDetailId(d.id); }} />}
            {reseedOpen && <QaSampleReseedDialog defaultClientId={QA_CLIENT_ID} onClose={() => setReseedOpen(false)} onDone={load} />}
            {detailId && <StudyBuildDetail buildId={detailId} onClose={() => setDetailId(null)} onChanged={(d) => setItems((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d, build: undefined } : x)))} />}
        </div>
    );
}

function Row({ b, onOpen, onChanged }) {
    const s = b.summary || {};
    const srcs = b.request?.sources || {};
    const kinds = ["protocol", "budget", "cta"].filter((k) => srcs[k] || b[`${k}_id`]);
    const ds = s.discrepancies || {};
    return (
        <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={onOpen} data-testid={`study-build-row-${b.id}`}>
            <td className="px-4 py-3">
                <div className="font-medium">{s.protocol_number || <span className="font-mono text-xs text-white/60">{b.id.slice(0, 8)}</span>}</div>
                {s.title && <div className="text-xs text-white/40 truncate max-w-xs">{s.title}</div>}
            </td>
            <td className="px-4 py-3"><StatusChip status={b.status} testId={`study-build-status-${b.id}`} /></td>
            <td className="px-4 py-3 text-white/70">{b.client_id}</td>
            <td className="px-4 py-3 text-xs text-white/50">{kinds.map((k) => `${k}${srcs[k]?.mode === "upload" ? " ↑" : ""}`).join(" · ")}</td>
            <td className="px-4 py-3 text-right tabular-nums text-white/70">{s.visits ?? "—"}</td>
            <td className="px-4 py-3 text-right tabular-nums text-emerald-400">{s.totals?.grand_total_usd != null ? `$${Number(s.totals.grand_total_usd).toLocaleString()}` : "—"}</td>
            <td className="px-4 py-3 text-xs">
                {["failed", "cancelled"].includes(b.status) ? <span className={`${b.status === "failed" ? "text-rose-300" : "text-white/45"} truncate block max-w-xs`} title={b.error}>{b.error}</span>
                    : ds.total != null ? (
                        <span className={`inline-flex items-center gap-1 ${ds.high ? "text-rose-300" : ds.medium ? "text-amber-300" : "text-white/50"}`} data-testid={`study-build-disc-${b.id}`}>
                            {ds.total > 0 && <AlertTriangle className="w-3 h-3" />} {ds.total} {ds.high ? `· ${ds.high} high` : ""}
                        </span>
                    ) : "—"}
            </td>
            <td className="px-4 py-3 text-white/50">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
            <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}><BuildActions build={b} compact onDone={onChanged} /></td>
        </tr>
    );
}
